import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo } from 'react';
import { map } from 'rxjs';

import { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import type { Workspace } from '../entities/workspace';
import type { WorkspaceImpl } from '../impls/workspace';
import { isDatabaseSystemCollectionDocId } from '../utils/database-row-shard-docs';

/**
 * sl-oiya.1.1: Bridge from AccessibleDocCacheStore to DocCollection.
 *
 * For ALL cloud workspaces (Owner, Admin, Member), the server-backed
 * AccessibleDocCacheStore is the single source of truth for which docs exist.
 * This hook watches the cache and registers/unregisters docs in DocCollection
 * so that doc open, navigation, and the block editor work correctly.
 *
 * Local workspaces don't need this — they get docs from Y.js root doc meta.pages.
 */
export function useAccessibleDocBridge(workspace: Workspace | null): void {
  const accessibleDocCache = useService(AccessibleDocCacheStore);

  // Derive doc IDs as LiveData from the Observable
  const accessibleDocIds$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAccessibleDocIds().pipe(map(ids => ids)),
        [] as string[]
      ),
    [accessibleDocCache]
  );
  const accessibleDocIds = useLiveData(accessibleDocIds$);
  const isServerConfirmed = useLiveData(accessibleDocCache.isServerConfirmed$);

  // Active for ALL cloud workspaces (Owner, Admin, Member), skip Local
  const isCloudPath = workspace && workspace.flavour !== 'local';

  useEffect(() => {
    if (!isCloudPath || !workspace) {
      return;
    }

    const docCollection = workspace.docCollection as unknown as WorkspaceImpl;

    // Register all accessible docs that aren't already in blockCollections
    for (const docId of accessibleDocIds) {
      const docMeta = accessibleDocCache.getCachedDoc(docId);
      docCollection.registerDoc(docId, {
        title: docMeta?.title,
        createDate: docMeta?.createDate,
        updatedDate: docMeta?.updatedDate,
        trash: docMeta?.trashed,
      });
    }

    if (!isServerConfirmed) {
      return;
    }

    // Unregister docs that are no longer accessible after server confirmation.
    const accessibleSet = new Set(accessibleDocIds);
    for (const docId of docCollection.blockCollections.keys()) {
      if (isDatabaseSystemCollectionDocId(docId, workspace.id)) {
        continue;
      }
      if (!accessibleSet.has(docId)) {
        docCollection.unregisterDoc(docId);
      }
    }
  }, [
    isCloudPath,
    workspace,
    accessibleDocIds,
    isServerConfirmed,
    accessibleDocCache,
  ]);
}
